import {
  axisFor,
  bucketSeries,
  buildSeries,
  dayKey,
  describeQuantity,
  quantitativeRange,
  type Aggregate,
  type BucketSize,
  type Entry,
  type Track,
} from '@anytracker/core'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '../components/Button.js'
import { formatDate, formatDateTime } from '../format/dates.js'
import { ChartFrame, type FrameTick } from './ChartFrame.js'
import styles from './Charts.module.css'
import { categoryColour, trackColour } from './colours.js'
import {
  CHART,
  PLOT_BOTTOM,
  PLOT_HEIGHT,
  countTicks,
  linePath,
  xOf,
  yOf,
  type Span,
} from './geometry.js'
import { StatisticsPanel } from './StatisticsPanel.js'

interface TrackChartProps {
  readonly track: Track
  readonly entries: readonly Entry[]
  readonly span: Span
  readonly size: BucketSize
  readonly aggregate: Aggregate
  readonly deviceZone: string
}

/**
 * One track over time (T-403).
 *
 * The chart type follows the value type and cannot be overridden. A line
 * through booleans is meaningless; a line through unordered categories is worse
 * than meaningless, because it looks like it means something — the eye reads a
 * slope between `breakfast` and `dinner` that exists only because they were
 * listed in that order.
 *
 * Every number here comes from `@anytracker/core`. Nothing is computed in this
 * component, so the chart and the PDF report cannot disagree.
 */
export function TrackChart({
  track,
  entries,
  span,
  size,
  aggregate,
  deviceZone,
}: TrackChartProps) {
  const navigate = useNavigate()
  const [selected, setSelected] = useState<string | null>(null)

  const series = useMemo(
    () => buildSeries(track, entries, { fromEpochMs: span.from, toEpochMs: span.to }),
    [track, entries, span.from, span.to],
  )
  const bucketed = useMemo(
    () => bucketSeries(series, { size, aggregate }),
    [series, size, aggregate],
  )

  const timeTicks = useMemo(() => {
    const middle = span.from + (span.to - span.from) / 2
    return [span.from, middle, span.to].map((epochMs) => ({
      epochMs,
      label: formatDate({ epochMs, timeZone: deviceZone }),
    }))
  }, [span.from, span.to, deviceZone])

  const colour = trackColour(track.id)

  if (bucketed.buckets.length === 0) {
    return (
      <section className={styles['card']}>
        <h2 className={styles['title']}>{track.name}</h2>
        <p className={styles['nothing']}>
          Nothing recorded for {track.name} in this range.
        </p>
      </section>
    )
  }

  const chart =
    bucketed.kind === 'categorical' ? (
      <CategoryMarks
        buckets={bucketed.buckets}
        span={span}
        onSelect={setSelected}
        selected={selected}
      />
    ) : track.kind.kind === 'boolean' ? (
      <RateMarks
        buckets={bucketed.buckets}
        span={span}
        colour={colour}
        onSelect={setSelected}
        selected={selected}
      />
    ) : (
      <LineMarks
        buckets={bucketed.buckets}
        span={span}
        colour={colour}
        range={quantitativeRange(track) ?? { min: 0, max: 1 }}
        onSelect={setSelected}
        selected={selected}
      />
    )

  const { valueRange, valueTicks, label } = describeAxis(track, bucketed)
  const chosen = bucketed.buckets.find((bucket) => bucket.bucket.key === selected)

  return (
    <section className={styles['card']}>
      <div className={styles['head']}>
        <h2 className={styles['title']}>
          <span className={styles['swatch']} style={{ background: colour }} />
          {track.name}
          {track.unit === undefined ? '' : ` (${track.unit})`}
        </h2>
        <span className={styles['kindNote']}>{chartNote(track, aggregate, size)}</span>
      </div>

      <ChartFrame
        label={label}
        valueRange={valueRange}
        valueTicks={valueTicks}
        span={span}
        timeTicks={timeTicks}
      >
        {chart}
      </ChartFrame>

      {series.unresolved > 0 && (
        <p className={styles['warning']}>
          {series.unresolved === 1
            ? '1 measurement could not be read against this track as it is now.'
            : `${String(series.unresolved)} measurements could not be read against this track as it is now.`}
        </p>
      )}

      {chosen !== undefined && (
        <div className={styles['selection']}>
          <div className={styles['selectionHead']}>
            <strong>{chosen.bucket.key}</strong>
            <Button
              onClick={() => {
                const from = dayKey({
                  epochMs: chosen.bucket.startEpochMs,
                  timeZone: chosen.bucket.timeZone,
                })
                const to = dayKey({
                  epochMs: chosen.bucket.endEpochMs - 1,
                  timeZone: chosen.bucket.timeZone,
                })
                void navigate(`/timeline?track=${track.id}&from=${from}&to=${to}`)
              }}
            >
              Show in timeline
            </Button>
          </div>
          <ul className={styles['selectionList']}>
            {chosen.points.map((point) => (
              <li key={point.entryId}>
                <span>{formatDateTime(point.at)}</span>
                <strong>{point.label}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      <StatisticsPanel series={series} track={track} />

      {/*
        The accessible fallback the ticket requires, and the honest one: the
        chart is `role="img"`, so this is the only way to reach the values with
        a screen reader — and it is also the fastest way for anyone to read an
        exact number off a chart.
      */}
      <details className={styles['tableWrap']}>
        <summary>Show the numbers</summary>
        <table className={styles['table']}>
          <thead>
            <tr>
              <th scope="col">Period</th>
              <th scope="col">{bucketed.kind === 'categorical' ? 'Counts' : 'Value'}</th>
              <th scope="col">Entries</th>
            </tr>
          </thead>
          <tbody>
            {bucketed.kind === 'categorical'
              ? bucketed.buckets.map((bucket) => (
                  <tr key={bucket.bucket.key}>
                    <th scope="row">{bucket.bucket.key}</th>
                    <td>
                      {bucket.counts
                        .map((count) => `${count.label} ${String(count.count)}`)
                        .join(', ')}
                    </td>
                    <td>{bucket.total}</td>
                  </tr>
                ))
              : bucketed.buckets.map((bucket) => (
                  <tr key={bucket.bucket.key}>
                    <th scope="row">{bucket.bucket.key}</th>
                    <td>{describeQuantity(track, bucket.value)}</td>
                    <td>{bucket.count}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </details>
    </section>
  )
}

// ─── Axis description ─────────────────────────────────────────────────────────

type Bucketed = ReturnType<typeof bucketSeries>

function describeAxis(
  track: Track,
  bucketed: Bucketed,
): { valueRange: { min: number; max: number }; valueTicks: FrameTick[]; label: string } {
  if (bucketed.kind === 'categorical') {
    const max = Math.max(...bucketed.buckets.map((bucket) => bucket.total), 1)
    const ticks = countTicks(max)
    return {
      valueRange: { min: 0, max: ticks[ticks.length - 1] ?? max },
      valueTicks: ticks.map((quantity) => ({ quantity, label: String(quantity) })),
      label: `${track.name}: counts of each option per period.`,
    }
  }

  if (track.kind.kind === 'boolean') {
    // A rate axis, not the track's own 0/1: with several entries in a period the
    // interesting number is how often, and 0/1 labels would be a lie about a
    // bar sitting at 0.6.
    return {
      valueRange: { min: 0, max: 1 },
      valueTicks: [
        { quantity: 0, label: '0%' },
        { quantity: 0.5, label: '50%' },
        { quantity: 1, label: '100%' },
      ],
      label: `${track.name}: how often per period.`,
    }
  }

  const axis = axisFor(track)
  if (axis === null) {
    return {
      valueRange: { min: 0, max: 1 },
      valueTicks: [],
      label: `${track.name} over time.`,
    }
  }
  return {
    valueRange: { min: axis.min, max: axis.max },
    // An ordered enum's ticks are its option labels, never the scores behind
    // them — the scores are an implementation detail nobody chose.
    valueTicks: [...axis.ticks],
    label: `${track.name} over time.`,
  }
}

function chartNote(track: Track, aggregate: Aggregate, size: BucketSize): string {
  const per = `per ${size}`
  switch (track.kind.kind) {
    case 'boolean':
      return `Rate ${per}`
    case 'enum':
      return track.kind.ordered ? `${aggregate} ${per}` : `Counts ${per}`
    case 'numeric':
      return `${aggregate} ${per}`
  }
}

// ─── Marks ────────────────────────────────────────────────────────────────────

interface MarkProps<B> {
  readonly buckets: readonly B[]
  readonly span: Span
  readonly selected: string | null
  readonly onSelect: (key: string | null) => void
}

type QuantitativeBuckets = Extract<Bucketed, { kind: 'quantitative' }>['buckets']
type CategoricalBuckets = Extract<Bucketed, { kind: 'categorical' }>['buckets']

function LineMarks({
  buckets,
  span,
  colour,
  range,
  selected,
  onSelect,
}: MarkProps<QuantitativeBuckets[number]> & {
  readonly colour: string
  readonly range: { min: number; max: number }
}) {
  const points = buckets.map((bucket) => ({
    x: xOf(bucket.bucket.startEpochMs, span),
    y: yOf(bucket.value, range),
    followsGap: bucket.followsGap,
    key: bucket.bucket.key,
  }))

  return (
    <>
      <path d={linePath(points)} fill="none" stroke={colour} className={styles['line']} />
      {points.map((point) => (
        <circle
          key={point.key}
          cx={point.x}
          cy={point.y}
          r={selected === point.key ? 5 : 3}
          fill={colour}
          className={styles['point']}
          onClick={() => {
            onSelect(selected === point.key ? null : point.key)
          }}
        >
          <title>{point.key}</title>
        </circle>
      ))}
    </>
  )
}

function RateMarks({
  buckets,
  span,
  colour,
  selected,
  onSelect,
}: MarkProps<QuantitativeBuckets[number]> & { readonly colour: string }) {
  return (
    <>
      {buckets.map((bucket) => {
        const left = xOf(bucket.bucket.startEpochMs, span)
        const right = xOf(bucket.bucket.endEpochMs, span)
        const width = Math.max(right - left - 1, 1.5)
        const y = yOf(bucket.value, { min: 0, max: 1 })
        return (
          <rect
            key={bucket.bucket.key}
            x={left}
            y={y}
            width={width}
            height={Math.max(PLOT_BOTTOM - y, 0)}
            fill={colour}
            opacity={selected === bucket.bucket.key ? 1 : 0.85}
            className={styles['bar']}
            onClick={() => {
              onSelect(selected === bucket.bucket.key ? null : bucket.bucket.key)
            }}
          >
            <title>{bucket.bucket.key}</title>
          </rect>
        )
      })}
    </>
  )
}

function CategoryMarks({
  buckets,
  span,
  selected,
  onSelect,
}: MarkProps<CategoricalBuckets[number]>) {
  const max = Math.max(...buckets.map((bucket) => bucket.total), 1)
  const ticks = countTicks(max)
  const top = ticks[ticks.length - 1] ?? max
  const optionOrder = [
    ...new Set(buckets.flatMap((bucket) => bucket.counts.map((count) => count.optionId))),
  ]

  return (
    <>
      {buckets.map((bucket) => {
        const left = xOf(bucket.bucket.startEpochMs, span)
        const right = xOf(bucket.bucket.endEpochMs, span)
        const width = Math.max(right - left - 1, 1.5)
        let stacked = 0
        return (
          <g
            key={bucket.bucket.key}
            onClick={() => {
              onSelect(selected === bucket.bucket.key ? null : bucket.bucket.key)
            }}
          >
            {[...bucket.counts]
              .sort(
                (a, b) =>
                  optionOrder.indexOf(a.optionId) - optionOrder.indexOf(b.optionId),
              )
              .map((count) => {
                const height = (count.count / top) * PLOT_HEIGHT
                stacked += height
                return (
                  <rect
                    key={count.optionId}
                    x={left}
                    y={CHART.padTop + PLOT_HEIGHT - stacked}
                    width={width}
                    height={Math.max(height, 0)}
                    fill={categoryColour(optionOrder.indexOf(count.optionId))}
                    opacity={selected === bucket.bucket.key ? 1 : 0.85}
                    className={styles['bar']}
                  >
                    <title>{`${count.label}: ${String(count.count)}`}</title>
                  </rect>
                )
              })}
          </g>
        )
      })}
    </>
  )
}
